import { DenemeSection, DenemeVideo } from '../services/api';

/**
 * Normalise the two shapes `/public/deneme-books/:id` can return into ONE
 * "subject → soru listesi" model for the public Deneme Sınavı video viewer.
 *
 * The exam books were imported by two different content passes and both shapes
 * are live at the same time:
 *
 *   A. One section per SUBJECT, carrying every solution video as `contents`.
 *      section_topic = "Fen Bilimleri", section_category = "Dersler",
 *      videos = [{title: "Soru 1"}, …]. (e.g. TYT 02 Denemesi)
 *
 *   B. One section per QUESTION — the node tree has a node per soru, so the
 *      backend emits 125–166 sections, each with a single `lesson_video_url`
 *      and no contents. section_topic = "1", "2", … and the subject only
 *      survives in section_category ("Türkçe", "Temel Matematik", …).
 *      (e.g. TYT 01 / 03–06, AYT 01–04, TYT Hazırlık 1–2)
 *
 * Shape B is what made the viewer unusable: 125 subject tabs named "1".."125",
 * each opening a single tile labelled "Ders Videosu". Grouping happens here so
 * the page renders identically either way — subject heading + "Soru N" tiles.
 */

export interface DenemeGroupVideo {
  /** Stable key across re-renders; also the player's identity. */
  id: string;
  /** Question number when we could derive one — used for the "Soru N" caption. */
  number: number | null;
  /** Rendered caption: "Soru 12", or the authored title when unnumbered. */
  label: string;
  url: string;
  /** Owning group's key — identity, safe to compare even if two titles match. */
  groupKey: string;
  /** Owning subject title, so the full-screen player can show it in its header. */
  groupTitle: string;
}

export interface DenemeVideoGroup {
  key: string;
  /** Subject heading — "Türkçe", "Temel Matematik", … */
  title: string;
  videos: DenemeGroupVideo[];
}

/** "12" → 12, "Soru 12" → 12, "Türkçe Soru 12" → 12, "Fen Bilimleri" → null. */
export function parseQuestionNumber(text: string | null | undefined): number | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  // "Soru 12" / "SORU 12" / "… soru 12". Turkish dotless ı never appears in
  // "soru", so a plain case-insensitive match is safe here.
  const soru = trimmed.match(/soru\s*(\d+)\s*$/i);
  if (soru) return Number(soru[1]);
  return null;
}

/** "Türkçe Soru 12" → "Türkçe". Used only when section_category is blank. */
function stripQuestionSuffix(label: string | null | undefined): string {
  if (!label) return '';
  return label.replace(/\s*soru\s*\d+\s*$/i, '').trim();
}

/** The section's own heading, ignoring the per-question numbering. */
function sectionHeading(section: DenemeSection): string {
  return (section.section_topic || section.section_display_label || section.section_label || '').trim();
}

/** Flatten a section into playable entries; the lesson video comes first. */
function sectionVideos(section: DenemeSection, sectionIndex: number): Array<{ id: string; number: number | null; title: string; url: string }> {
  const out: Array<{ id: string; number: number | null; title: string; url: string }> = [];
  if (section.lesson_video_url) {
    // Shape B: the "lesson video" IS the solution video for this one question,
    // so its number lives on the section, not on the video.
    out.push({
      id: `s${sectionIndex}-lesson`,
      number: parseQuestionNumber(section.section_topic)
        ?? parseQuestionNumber(section.section_display_label)
        ?? parseQuestionNumber(section.section_label),
      title: sectionHeading(section),
      url: section.lesson_video_url,
    });
  }
  (section.videos ?? []).forEach((v: DenemeVideo, i) => {
    if (!v.url) return;
    out.push({
      id: `s${sectionIndex}-v${v.id ?? i}`,
      number: parseQuestionNumber(v.short_title) ?? parseQuestionNumber(v.title),
      title: (v.short_title || v.title || '').trim(),
      url: v.url,
    });
  });
  return out;
}

/**
 * A section that stands for a single question rather than a whole subject:
 * exactly one video and a heading that is just a number ("1") or ends in
 * "Soru 12". A subject-shaped section ("Fen Bilimleri" + 20 videos) is not one,
 * even when it happens to carry a single video.
 */
function isQuestionSection(section: DenemeSection, videoCount: number): boolean {
  if (videoCount !== 1) return false;
  return parseQuestionNumber(sectionHeading(section)) != null;
}

type Run = {
  subject: string;
  fallbackSubject: string;
  videos: DenemeGroupVideo[];
  lastNumber: number | null;
};

/**
 * Group the ordered section stream into subject → soru lists.
 *
 * Question-sections are merged into a run while they keep the same
 * `section_category` AND their numbers keep climbing. A number that does not
 * increase means the paper moved on to the next subject (Türkçe 40 → Sosyal 1),
 * which is the split the content team relies on even when the category label is
 * repeated. Subject-shaped sections always stand alone as their own group.
 */
export function groupDenemeVideos(sections: DenemeSection[]): DenemeVideoGroup[] {
  const runs: Run[] = [];
  let open: Run | null = null;

  (sections ?? []).forEach((section, index) => {
    const entries = sectionVideos(section, index);
    if (entries.length === 0) return;

    if (!isQuestionSection(section, entries.length)) {
      // Subject-shaped section: it is already a group.
      open = null;
      const title = sectionHeading(section) || (section.section_category ?? '').trim();
      runs.push({
        subject: title,
        fallbackSubject: title,
        lastNumber: null,
        videos: entries.map((e) => ({
          id: e.id,
          number: e.number,
          label: e.number != null ? `Soru ${e.number}` : (e.title || 'Video'),
          url: e.url,
          groupKey: '', // filled in below, once collisions are resolved
          groupTitle: title,
        })),
      });
      return;
    }

    const entry = entries[0];
    const subject = (section.section_category ?? '').trim();
    const fallbackSubject = stripQuestionSuffix(section.section_display_label || section.section_label);
    const restarted = open !== null
      && entry.number != null
      && open.lastNumber != null
      && entry.number <= open.lastNumber;

    let run = open;
    if (run === null || run.subject !== subject || restarted) {
      run = { subject, fallbackSubject, videos: [], lastNumber: null };
      runs.push(run);
      open = run;
    }
    run.videos.push({
      id: entry.id,
      number: entry.number,
      label: entry.number != null ? `Soru ${entry.number}` : (entry.title || `Video ${run.videos.length + 1}`),
      url: entry.url,
      groupKey: '',   // both filled in below, once collisions are resolved
      groupTitle: '',
    });
    if (entry.number != null) run.lastNumber = entry.number;
  });

  // Title resolution. `section_category` is the authored subject and wins, but
  // it can be a generic container ("Dersler") shared by every run — in that
  // case the label prefix ("Türkçe Soru 1" → "Türkçe") tells the runs apart,
  // and an ordinal is the last resort so two headings are never identical.
  const byCategory = new Map<string, number>();
  runs.forEach((r) => byCategory.set(r.subject, (byCategory.get(r.subject) ?? 0) + 1));

  const used = new Map<string, number>();
  return runs.map((run, i) => {
    const ambiguous = !run.subject || (byCategory.get(run.subject) ?? 0) > 1;
    let title = (ambiguous && run.fallbackSubject) ? run.fallbackSubject : (run.subject || run.fallbackSubject);
    if (!title) title = `${i + 1}. Bölüm`;
    const seen = used.get(title) ?? 0;
    used.set(title, seen + 1);
    if (seen > 0) title = `${title} (${seen + 1})`;
    const key = `g${i}`;
    return {
      key,
      title,
      videos: run.videos.map((v) => ({ ...v, groupKey: key, groupTitle: title })),
    };
  });
}

/** Every video across every group, in reading order — the player's playlist. */
export function flattenDenemeGroups(groups: DenemeVideoGroup[]): DenemeGroupVideo[] {
  return groups.flatMap((g) => g.videos);
}
